import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";

// ── Stat Card ──────────────────────────────────────────────────────────────
const StatCard = ({ number, label }) => (
  <div className="flex flex-col items-center justify-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300">
    <span className="text-4xl md:text-5xl font-extrabold text-yellow-400 font-philosopher">
      {number}
    </span>
    <span className="text-sm md:text-base text-white/80 mt-2 text-center font-poppins">
      {label}
    </span>
  </div>
);

// ── Value Card ─────────────────────────────────────────────────────────────
const ValueCard = ({ icon, title, desc, delay }) => (
  <div
    data-aos="fade-up"
    data-aos-delay={delay}
    className="group relative bg-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition-all duration-500 border border-orange-50 overflow-hidden"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-yellow-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="relative z-10">
      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-2xl mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300">
        {icon}
      </div>
      <h3 className="font-philosopher text-lg font-bold text-[#232F3E] mb-2">
        {title}
      </h3>
      <p className="text-gray-500 text-sm leading-relaxed font-poppins">{desc}</p>
    </div>
  </div>
);

// ── Team Card ──────────────────────────────────────────────────────────────
const TeamCard = ({ name, role, emoji, delay }) => (
  <div
    data-aos="zoom-in"
    data-aos-delay={delay}
    className="flex flex-col items-center text-center group"
  >
    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-4xl shadow-lg mb-4 group-hover:scale-110 transition-transform duration-300">
      {emoji}
    </div>
    <h4 className="font-philosopher font-bold text-[#232F3E] text-base">{name}</h4>
    <p className="text-orange-500 text-xs font-poppins mt-1">{role}</p>
  </div>
);

// ── Main About Page ────────────────────────────────────────────────────────
const AboutPage = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-out", once: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ── All text content with translations ──
  const content = {
    en: {
      heroTag: "Our Story",
      heroTitle: "Crafting India's Heritage,\nOne Artisan at a Time",
      heroSubtitle:
        "Bharat Karigari is a multi-vendor marketplace that bridges the gap between India's finest artisans and art lovers across the world. Every product tells a story of skill, tradition, and passion.",
      missionTag: "Our Mission",
      missionTitle: "Empowering Artisans,\nPreserving Traditions",
      missionText1:
        "We believe that every handcrafted piece carries the soul of its maker. From the stone sculptures of Rajasthan to the wooden art of Kashmir, from metal masterpieces of Moradabad to the intricate handicrafts of Bengal — Bharat Karigari is a celebration of India's artistic heritage.",
      missionText2:
        "Our platform empowers thousands of artisans by giving them a direct channel to reach customers globally, eliminating middlemen and ensuring fair compensation for their extraordinary craft.",
      statsArtisans: "Artisans",
      statsProducts: "Products",
      statsStates: "States Covered",
      statsCustomers: "Happy Customers",
      valuesTag: "Why Choose Us",
      valuesTitle: "Built on Trust,\nCrafted with Purpose",
      values: [
        {
          icon: "🏺",
          title: "100% Authentic",
          desc: "Every product is handcrafted by verified artisans. No mass production. No imitations. Pure Indian craftsmanship.",
        },
        {
          icon: "🤝",
          title: "Multi-Vendor Platform",
          desc: "Hundreds of independent artisans and craft sellers operate on our platform, giving you unparalleled variety.",
        },
        {
          icon: "🌿",
          title: "Sustainable Craft",
          desc: "We promote eco-friendly materials and traditional techniques that have been passed down through generations.",
        },
        {
          icon: "💰",
          title: "Fair to Artisans",
          desc: "We ensure artisans receive fair prices for their work. When you buy here, you directly support a craftsperson's family.",
        },
        {
          icon: "🚚",
          title: "Pan-India Delivery",
          desc: "From the mountains of Himachal to the shores of Kerala, we deliver across all 28 states and 8 union territories.",
        },
        {
          icon: "🔒",
          title: "Secure & Trusted",
          desc: "Shop with confidence. Secure payments, easy returns, and a buyer protection policy on every order.",
        },
      ],
      journeyTag: "Our Journey",
      journeyTitle: "From a Dream to\nIndia's Artisan Marketplace",
      journeySteps: [
        {
          year: "2023",
          title: "The Idea",
          desc: "Founded with a vision to connect India's artisans with the world.",
        },
        {
          year: "2024",
          title: "Platform Launch",
          desc: "Launched with 50+ artisans across 10 categories — handicrafts, stone, wood, metal and more.",
        },
        {
          year: "2025",
          title: "Going Global",
          desc: "Expanded to international shipping. Artisans from 15+ states now sell on our platform.",
        },
        {
          year: "2026",
          title: "Today",
          desc: "Thousands of products, hundreds of artisans, and a growing community of art lovers worldwide.",
        },
      ],
      teamTag: "The People Behind",
      teamTitle: "Our Passionate Team",
      ctaTitle: "Want to Sell on Bharat Karigari?",
      ctaSubtitle:
        "Are you an artisan or a craft business? Join our growing community of sellers and reach millions of art lovers across India and the world.",
      ctaButton: "Become a Seller",
      ctaButton2: "Browse Products",
    },
    hi: {
      heroTag: "हमारी कहानी",
      heroTitle: "भारत की विरासत को\nसंजोते हुए, एक कारीगर के साथ",
      heroSubtitle:
        "भारत कारीगरी एक मल्टी-वेंडर मार्केटप्लेस है जो भारत के सर्वश्रेष्ठ कारीगरों और दुनिया भर के कला प्रेमियों के बीच की खाई को पाटता है।",
      missionTag: "हमारा मिशन",
      missionTitle: "कारीगरों को सशक्त बनाना,\nपरंपराओं को संरक्षित करना",
      missionText1:
        "हम मानते हैं कि हर हस्तनिर्मित वस्तु अपने निर्माता की आत्मा को वहन करती है। राजस्थान की पत्थर की मूर्तियों से लेकर कश्मीर की लकड़ी की कला तक — भारत कारीगरी भारत की कलात्मक विरासत का उत्सव है।",
      missionText2:
        "हमारा प्लेटफॉर्म हजारों कारीगरों को वैश्विक स्तर पर ग्राहकों तक पहुंचने का सीधा मार्ग देकर सशक्त बनाता है।",
      statsArtisans: "कारीगर",
      statsProducts: "उत्पाद",
      statsStates: "राज्य",
      statsCustomers: "खुश ग्राहक",
      valuesTag: "हमें क्यों चुनें",
      valuesTitle: "विश्वास पर निर्मित,\nउद्देश्य से तैयार",
      values: [
        { icon: "🏺", title: "100% प्रामाणिक", desc: "हर उत्पाद सत्यापित कारीगरों द्वारा हस्तनिर्मित है।" },
        { icon: "🤝", title: "मल्टी-वेंडर प्लेटफॉर्म", desc: "सैकड़ों स्वतंत्र कारीगर हमारे प्लेटफॉर्म पर काम करते हैं।" },
        { icon: "🌿", title: "टिकाऊ शिल्प", desc: "हम पर्यावरण के अनुकूल सामग्री और पारंपरिक तकनीकों को बढ़ावा देते हैं।" },
        { icon: "💰", title: "कारीगरों के लिए उचित", desc: "हम सुनिश्चित करते हैं कि कारीगरों को उनके काम के लिए उचित मूल्य मिले।" },
        { icon: "🚚", title: "पूरे भारत में डिलीवरी", desc: "सभी 28 राज्यों और 8 केंद्र शासित प्रदेशों में डिलीवरी।" },
        { icon: "🔒", title: "सुरक्षित और विश्वसनीय", desc: "सुरक्षित भुगतान, आसान वापसी और खरीदार सुरक्षा नीति।" },
      ],
      journeyTag: "हमारी यात्रा",
      journeyTitle: "एक सपने से\nभारत के कारीगर बाज़ार तक",
      journeySteps: [
        { year: "2023", title: "विचार", desc: "भारत के कारीगरों को विश्व से जोड़ने की दृष्टि के साथ स्थापित।" },
        { year: "2024", title: "लॉन्च", desc: "10 श्रेणियों में 50+ कारीगरों के साथ लॉन्च।" },
        { year: "2025", title: "वैश्विक विस्तार", desc: "अंतर्राष्ट्रीय शिपिंग तक विस्तार। 15+ राज्यों के कारीगर।" },
        { year: "2026", title: "आज", desc: "हजारों उत्पाद, सैकड़ों कारीगर और बढ़ता समुदाय।" },
      ],
      teamTag: "हमारे पीछे के लोग",
      teamTitle: "हमारी उत्साही टीम",
      ctaTitle: "भारत कारीगरी पर बेचना चाहते हैं?",
      ctaSubtitle: "क्या आप एक कारीगर या शिल्प व्यवसाय हैं? हमारे बढ़ते विक्रेता समुदाय से जुड़ें।",
      ctaButton: "विक्रेता बनें",
      ctaButton2: "उत्पाद देखें",
    },
    es: {
      heroTag: "Nuestra Historia",
      heroTitle: "Preservando la Herencia\nde India, Artesano por Artesano",
      heroSubtitle:
        "Bharat Karigari es un mercado multi-vendedor que conecta a los mejores artesanos de India con amantes del arte en todo el mundo.",
      missionTag: "Nuestra Misión",
      missionTitle: "Empoderando Artesanos,\nPreservando Tradiciones",
      missionText1:
        "Creemos que cada pieza artesanal lleva el alma de su creador. Desde esculturas de piedra de Rajastán hasta arte en madera de Cachemira — Bharat Karigari es una celebración del patrimonio artístico de India.",
      missionText2:
        "Nuestra plataforma empodera a miles de artesanos dándoles un canal directo para llegar a clientes globalmente.",
      statsArtisans: "Artesanos",
      statsProducts: "Productos",
      statsStates: "Estados",
      statsCustomers: "Clientes Felices",
      valuesTag: "Por Qué Elegirnos",
      valuesTitle: "Construido en Confianza,\nCreado con Propósito",
      values: [
        { icon: "🏺", title: "100% Auténtico", desc: "Cada producto está hecho a mano por artesanos verificados." },
        { icon: "🤝", title: "Plataforma Multi-Vendedor", desc: "Cientos de artesanos independientes operan en nuestra plataforma." },
        { icon: "🌿", title: "Artesanía Sostenible", desc: "Promovemos materiales ecológicos y técnicas tradicionales." },
        { icon: "💰", title: "Justo para Artesanos", desc: "Aseguramos precios justos para los artesanos." },
        { icon: "🚚", title: "Entrega en toda India", desc: "Entregamos en los 28 estados y 8 territorios." },
        { icon: "🔒", title: "Seguro y Confiable", desc: "Pagos seguros, devoluciones fáciles y protección al comprador." },
      ],
      journeyTag: "Nuestro Viaje",
      journeyTitle: "De un Sueño al\nMercado Artesanal de India",
      journeySteps: [
        { year: "2023", title: "La Idea", desc: "Fundado con la visión de conectar artesanos de India con el mundo." },
        { year: "2024", title: "Lanzamiento", desc: "Lanzado con 50+ artesanos en 10 categorías." },
        { year: "2025", title: "Expansión Global", desc: "Ampliado al envío internacional. 15+ estados representados." },
        { year: "2026", title: "Hoy", desc: "Miles de productos, cientos de artesanos y una comunidad creciente." },
      ],
      teamTag: "Las Personas Detrás",
      teamTitle: "Nuestro Apasionado Equipo",
      ctaTitle: "¿Quieres Vender en Bharat Karigari?",
      ctaSubtitle: "¿Eres artesano o negocio artesanal? Únete a nuestra comunidad de vendedores.",
      ctaButton: "Ser Vendedor",
      ctaButton2: "Ver Productos",
    },
    zh: {
      heroTag: "我们的故事",
      heroTitle: "传承印度工艺，\n一次一位工匠",
      heroSubtitle:
        "Bharat Karigari 是一个多供应商市场平台，连接印度最优秀的工匠与全球艺术爱好者。",
      missionTag: "我们的使命",
      missionTitle: "赋能工匠，\n传承传统",
      missionText1:
        "我们相信每件手工艺品都承载着创作者的灵魂。从拉贾斯坦邦的石雕到克什米尔的木艺——Bharat Karigari 是对印度艺术遗产的礼赞。",
      missionText2:
        "我们的平台为数千名工匠提供直接面向全球客户的渠道，消除中间商，确保公平报酬。",
      statsArtisans: "工匠",
      statsProducts: "产品",
      statsStates: "覆盖州",
      statsCustomers: "满意客户",
      valuesTag: "为什么选择我们",
      valuesTitle: "建立于信任，\n创造于目标",
      values: [
        { icon: "🏺", title: "100% 正品", desc: "每件产品均由经过认证的工匠手工制作。" },
        { icon: "🤝", title: "多供应商平台", desc: "数百名独立工匠在我们的平台上经营。" },
        { icon: "🌿", title: "可持续工艺", desc: "我们推广环保材料和代代相传的传统技术。" },
        { icon: "💰", title: "对工匠公平", desc: "我们确保工匠获得公平的报酬。" },
        { icon: "🚚", title: "全印度配送", desc: "覆盖全印度28个邦和8个联邦直辖区。" },
        { icon: "🔒", title: "安全可信", desc: "安全支付、便捷退货和买家保护政策。" },
      ],
      journeyTag: "我们的历程",
      journeyTitle: "从梦想到\n印度工匠市场",
      journeySteps: [
        { year: "2023", title: "创意诞生", desc: "以连接印度工匠与世界的愿景成立。" },
        { year: "2024", title: "平台上线", desc: "携50+工匠在10个品类正式上线。" },
        { year: "2025", title: "走向全球", desc: "扩展至国际配送，15+邦的工匠入驻。" },
        { year: "2026", title: "今天", desc: "数千件产品，数百名工匠，不断壮大的社区。" },
      ],
      teamTag: "幕后团队",
      teamTitle: "我们充满热情的团队",
      ctaTitle: "想在 Bharat Karigari 上销售？",
      ctaSubtitle: "您是工匠或手工艺企业吗？加入我们不断壮大的卖家社区。",
      ctaButton: "成为卖家",
      ctaButton2: "浏览产品",
    },
  };

  const c = content[currentLanguage] || content.en;

  const teamMembers = [
    { name: "Arjun Sharma", role: c.teamTag, emoji: "👨‍💼" },
    { name: "Priya Patel", role: "Design Lead", emoji: "👩‍🎨" },
    { name: "Rahul Verma", role: "Tech Lead", emoji: "👨‍💻" },
    { name: "Meera Nair", role: "Artisan Relations", emoji: "👩‍🤝‍👩" },
  ];

  return (
    <div className="bg-[#FAFAF8] overflow-hidden">
      {/*  HERO SECTION */}
      <section className="relative min-h-[90vh] bg-[#232F3E] flex items-center overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-yellow-400/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl" />
          {/* Mandala-inspired pattern */}
          <div className="absolute top-1/2 right-10 -translate-y-1/2 opacity-5 text-white text-[30rem] font-philosopher select-none pointer-events-none">
            ॐ
          </div>
        </div>

        <div className="relative z-10 w-full mx-auto px-6 md:px-10 py-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left text */}
            <div data-aos="fade-right">
              <span className="inline-block px-4 py-1.5 bg-yellow-400/20 border border-yellow-400/40 text-yellow-400 text-xs font-bold uppercase tracking-widest rounded-full mb-6">
                {c.heroTag}
              </span>
              <h1 className="font-philosopher text-4xl md:text-6xl font-bold text-white leading-tight mb-6 whitespace-pre-line">
                {c.heroTitle}
              </h1>
              <p className="text-white/70 text-lg font-poppins leading-relaxed max-w-lg">
                {c.heroSubtitle}
              </p>
              {/* Decorative divider */}
              <div className="flex items-center gap-3 mt-8">
                <div className="h-px w-12 bg-yellow-400" />
                <span className="text-yellow-400 text-sm font-poppins">
                  Bharat Karigari
                </span>
                <div className="h-px flex-1 bg-white/10" />
              </div>
            </div>

            {/* Right: Stats grid */}
            <div
              data-aos="fade-left"
              data-aos-delay="200"
              className="grid grid-cols-2 gap-4"
            >
              <StatCard number="500+" label={c.statsArtisans} />
              <StatCard number="5000+" label={c.statsProducts} />
              <StatCard number="15+" label={c.statsStates} />
              <StatCard number="10K+" label={c.statsCustomers} />
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 80L1440 80L1440 30C1200 80 960 0 720 30C480 60 240 0 0 30L0 80Z" fill="#FAFAF8" />
          </svg>
        </div>
      </section>

      {/* MISSION SECTION */}
      <section className="py-6 md:py-10 px-6 md:px-10 mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: decorative image mosaic */}
          <div data-aos="fade-right" className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="h-48 rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80"
                    alt="Artisan at work"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="h-32 rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&q=80"
                    alt="Handicraft"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="h-32 rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=400&q=80"
                    alt="Stone craft"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="h-48 rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=400&q=80"
                    alt="Indian art"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-6 bg-yellow-400 text-[#232F3E] px-5 py-3 rounded-2xl shadow-xl font-philosopher font-bold text-sm">
              🏺 Made in India
            </div>
          </div>

          {/* Right: text */}
          <div data-aos="fade-left" data-aos-delay="100">
            <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest rounded-full mb-4">
              {c.missionTag}
            </span>
            <h2 className="font-philosopher text-3xl md:text-4xl font-bold text-[#232F3E] leading-tight mb-6 whitespace-pre-line">
              {c.missionTitle}
            </h2>
            <p className="text-gray-600 font-poppins leading-relaxed mb-4">
              {c.missionText1}
            </p>
            <p className="text-gray-600 font-poppins leading-relaxed">
              {c.missionText2}
            </p>

            {/* Category pills */}
            <div className="flex flex-wrap gap-2 mt-6">
              {["🏺 Handicrafts", "🪨 Stone Art", "🪵 Wooden Art", "⚙️ Metal Craft", "🏠 Home Decor", "🍳 Kitchen Decor"].map(
                (cat) => (
                  <span
                    key={cat}
                    className="px-3 py-1.5 bg-[#232F3E] text-white text-xs rounded-full font-poppins"
                  >
                    {cat}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      </section>

      {/* VALUES SECTION */}
      <section className="bg-gradient-to-b from-orange-50/60 to-white px-6 md:px-10">
        <div className="mx-auto">
          <div className="text-center mb-14" data-aos="fade-up">
            <span className="inline-block px-4 py-1.5 bg-yellow-100 text-yellow-700 text-xs font-bold uppercase tracking-widest rounded-full mb-4">
              {c.valuesTag}
            </span>
            <h2 className="font-philosopher text-3xl md:text-4xl font-bold text-[#232F3E] whitespace-pre-line">
              {c.valuesTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.values.map((val, i) => (
              <ValueCard
                key={i}
                icon={val.icon}
                title={val.title}
                desc={val.desc}
                delay={i * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* JOURNEY / TIMELINE SECTION */}
      <section className="pb-10 px-6 md:px-10 bg-white">
        <div className="mx-auto">
          <div className="text-center mb-16" data-aos="fade-up">
            <span className="inline-block px-4 py-1.5 bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest rounded-full mb-4">
              {c.journeyTag}
            </span>
            <h2 className="font-philosopher text-3xl md:text-4xl font-bold text-[#232F3E] whitespace-pre-line">
              {c.journeyTitle}
            </h2>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-yellow-400 via-orange-400 to-orange-600 -translate-x-1/2" />

            <div className="space-y-12">
              {c.journeySteps.map((step, i) => (
                <div
                  key={i}
                  data-aos={i % 2 === 0 ? "fade-right" : "fade-left"}
                  data-aos-delay={i * 100}
                  className={`relative flex items-start gap-6 md:gap-0 ${
                    i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  {/* Dot */}
                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-yellow-400 border-4 border-white shadow-lg z-10 mt-1" />

                  {/* Content */}
                  <div
                    className={`ml-14 md:ml-0 md:w-5/12 ${
                      i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12 md:ml-auto"
                    }`}
                  >
                    <div className="bg-white rounded-2xl p-5 shadow-md border border-orange-50 hover:shadow-lg transition-shadow duration-300">
                      <span className="inline-block px-3 py-1 bg-yellow-400 text-[#232F3E] text-xs font-bold rounded-full mb-2">
                        {step.year}
                      </span>
                      <h3 className="font-philosopher font-bold text-[#232F3E] text-lg">
                        {step.title}
                      </h3>
                      <p className="text-gray-500 text-sm font-poppins mt-1 leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TEAM SECTION */}
      <section className="bg-orange-50/40 px-6 md:px-10">
        <div className="mx-auto">
          <div className="text-center mb-14" data-aos="fade-up">
            <span className="inline-block px-4 py-1.5 bg-yellow-100 text-yellow-700 text-xs font-bold uppercase tracking-widest rounded-full mb-4">
              {c.teamTag}
            </span>
            <h2 className="font-philosopher text-3xl md:text-4xl font-bold text-[#232F3E]">
              {c.teamTitle}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {teamMembers.map((member, i) => (
              <TeamCard
                key={i}
                name={member.name}
                role={member.role}
                emoji={member.emoji}
                delay={i * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative bg-[#232F3E] overflow-hidden px-6 md:px-10">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-yellow-400/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-5 text-white text-[20rem] font-philosopher select-none pointer-events-none">
            भ
          </div>
        </div>

        <div className="relative z-10  mx-auto text-center" data-aos="fade-up">
          <h2 className="font-philosopher text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {c.ctaTitle}
          </h2>
          <p className="text-white/70 font-poppins text-lg mb-10 leading-relaxed">
            {c.ctaSubtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/seller-registration"
              className="px-8 py-4 bg-yellow-400 text-[#232F3E] font-bold rounded-xl hover:bg-yellow-300 transition-colors duration-300 font-philosopher text-lg shadow-lg hover:shadow-yellow-400/30"
            >
              {c.ctaButton}
            </a>
            
            <a  
            href="/allproduct"
              className="px-8 py-4 bg-white/10 text-white font-bold rounded-xl hover:bg-white/20 transition-colors duration-300 font-philosopher text-lg border border-white/20"
            >
              {c.ctaButton2}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;